import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CTABanner from "../components/CTABanner";
import { motion } from "framer-motion";
import { BRAND } from "../data/products";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  ArrowRight,
  MessageCircle,
  CheckCircle2,
  Calendar,
  Navigation,
  ExternalLink,
  Download,
  Plus,
  Headphones,
  FileText,
  Building2,
  Wrench,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import Seo, { buildBreadcrumb, buildFaqSchema } from "../components/Seo";
import { PAGE_SEO } from "../data/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", subject: "", message: "" };

const FACTORY_BG = "/about-header-factory.png";
const WHATSAPP_NUMBER = "918866939091"; // E.164 without '+'
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi LIFTILE, I'd like to know more about your construction machinery."
)}`;

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: "easeOut" } },
};

/* ----------------------------- Hero ----------------------------- */
function ContactHero() {
  return (
    <section
      data-testid="contact-hero"
      className="relative bg-liftile-black overflow-hidden pt-32 md:pt-40 pb-20 md:pb-28"
    >
      {/* Background image with subtle slow zoom */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{
          backgroundImage: `url(${FACTORY_BG})`,
          animation: "contact-hero-zoom 28s ease-in-out infinite alternate",
        }}
      />
      {/* Cinematic overlay */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(10,10,10,0.82) 60%, rgba(0,0,0,0.92) 100%)",
        }}
      />
      {/* Soft orange ambient */}
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.22) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} className="max-w-3xl">
          <div className="heading-eyebrow mb-4">Get In Touch</div>
          <h1
            data-testid="contact-hero-headline"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
          >
            Contact <span className="text-liftile-orange">LIFTILE</span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: "easeOut" }}
            className="font-body text-liftile-cream/75 mt-7 text-base md:text-lg leading-relaxed max-w-2xl"
          >
            Whether you need a quotation, technical guidance, dealership
            information or product support, our engineering team is ready to
            assist you.
          </motion.p>
        </motion.div>
      </div>
      <style>{`
        @keyframes contact-hero-zoom {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.06); }
        }
      `}</style>
    </section>
  );
}

/* ----------------- Premium contact info cards ----------------- */
function ContactInfoCards() {
  const cards = [
    { icon: Phone, eyebrow: "Phone Number", value: BRAND.phone, href: `tel:${BRAND.phone.replace(/\s/g, "")}` },
    { icon: Mail, eyebrow: "Email Address", value: BRAND.email, href: `mailto:${BRAND.email}` },
    { icon: MapPin, eyebrow: "Manufacturing Facility", value: BRAND.address, href: BRAND.mapsUrl, target: "_blank" },
    { icon: Clock, eyebrow: "Business Hours", value: "Mon – Sat · 9:00 AM – 6:30 PM\nSunday · Closed" },
  ];
  return (
    <section data-testid="contact-info-cards" className="bg-liftile-matte-black py-16 md:py-20">
      <div className="container-x grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {cards.map((c, i) => {
          const Icon = c.icon;
          const inner = (
            <>
              <div className="w-12 h-12 rounded-xl grid place-items-center border border-liftile-orange/40 text-liftile-orange mb-5 group-hover:bg-liftile-orange group-hover:text-white transition-all duration-400">
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-orange/85 font-semibold mb-2">
                {c.eyebrow}
              </div>
              <div className="font-body text-sm md:text-[15px] text-liftile-cream whitespace-pre-line leading-snug">
                {c.value}
              </div>
              <div className="mt-4 w-10 h-0.5 bg-liftile-orange/70 transition-all duration-400 group-hover:w-16" />
            </>
          );
          return (
            <motion.div
              key={c.eyebrow}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.55, ease: "easeOut" }}
              data-testid={`contact-card-${c.eyebrow.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.07] rounded-2xl p-6 md:p-7 hover:bg-white/[0.07] hover:border-liftile-orange/50 hover:-translate-y-1 transition-all duration-400"
            >
              {c.href ? (
                <a href={c.href} target={c.target} rel={c.target ? "noopener noreferrer" : undefined} className="block">
                  {inner}
                </a>
              ) : (
                inner
              )}
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

/* ----------------- Premium contact form ----------------- */
function ContactFormBlock() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm(INITIAL);
      toast.success("Message sent. We'll get back to you within 1 business day.");
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="contact-form-section" className="bg-liftile-cream py-20 md:py-28 relative overflow-hidden">
      {/* Soft ambient */}
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.10]"
        style={{
          background: "radial-gradient(circle, rgba(255,107,26,0.65) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-16 items-start">
          {/* Left: factory image + intro */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp}>
            <div className="heading-eyebrow mb-4 text-liftile-orange">Send Us a Message</div>
            <h2
              data-testid="contact-form-heading"
              className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-3xl sm:text-4xl md:text-5xl"
            >
              How can our team
              <br />
              <span className="text-liftile-orange">help your project?</span>
            </h2>
            <p className="font-body text-liftile-matte-black/70 mt-6 text-base leading-relaxed max-w-md">
              Share your project details and our engineering team will respond
              with the best machine recommendation, technical specifications and
              a detailed quotation.
            </p>

            <div className="relative mt-9 rounded-2xl overflow-hidden shadow-[0_30px_70px_-25px_rgba(0,0,0,0.4)]">
              <img
                src={FACTORY_BG}
                alt="LIFTILE manufacturing facility, Vadodara"
                loading="lazy"
                className="w-full aspect-[5/4] object-cover"
              />
              <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-liftile-black/55 via-transparent to-transparent"
              />
              <div className="absolute bottom-4 left-5 right-5 flex items-center gap-3">
                <span className="block w-8 h-px bg-liftile-orange" />
                <span className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-cream">
                  Vadodara · Gujarat · India
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right: form OR success state */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            transition={{ delay: 0.1 }}
          >
            {sent ? (
              <motion.div
                key="sent"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                data-testid="contact-success-card"
                className="bg-white rounded-2xl p-9 md:p-12 border border-liftile-orange/25 shadow-[0_30px_60px_-20px_rgba(255,107,26,0.3)]"
              >
                <div className="w-16 h-16 rounded-full bg-liftile-orange/10 text-liftile-orange grid place-items-center mb-6">
                  <CheckCircle2 className="w-8 h-8" strokeWidth={1.7} />
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl text-liftile-matte-black leading-tight">
                  Message <span className="text-liftile-orange">received.</span>
                </h3>
                <p className="font-body text-liftile-matte-black/75 mt-4 text-base leading-relaxed">
                  Thank you for reaching out. Our engineering team has been
                  notified and will respond within{" "}
                  <span className="font-semibold text-liftile-matte-black">one business day</span>.
                  For urgent enquiries please call or message us directly using
                  the quick actions below.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another-btn"
                  className="mt-8 inline-flex items-center gap-2 font-heading uppercase tracking-wider text-xs text-liftile-orange hover:text-liftile-matte-black transition"
                >
                  <ArrowRight className="w-4 h-4" /> Send another message
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="bg-white rounded-2xl p-7 md:p-9 lg:p-10 border border-black/[0.06] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.15)] space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full Name *" testId="contact-name" value={form.name} onChange={update("name")} required />
                  <Field label="Email *" testId="contact-email" value={form.email} onChange={update("email")} type="email" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Phone" testId="contact-phone" value={form.phone} onChange={update("phone")} />
                  <Field label="Subject" testId="contact-subject" value={form.subject} onChange={update("subject")} />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.18em] font-heading text-liftile-matte-black/65 mb-2 font-semibold">
                    Project Details *
                  </label>
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={5}
                    placeholder="Tell us about your project — lifting height, load capacity, location, timeline…"
                    className="w-full bg-liftile-cream border border-black/10 rounded-lg px-4 py-3 text-liftile-matte-black placeholder-liftile-matte-black/40 focus:border-liftile-orange focus:ring-2 focus:ring-liftile-orange/15 focus:outline-none resize-none transition"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="group w-full inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-8 py-4 rounded-sm transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.85)] disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {loading ? "Sending..." : (<>Send Message <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>)}
                </button>
                <p className="text-[11px] text-liftile-matte-black/55 text-center pt-1">
                  We respond to all genuine enquiries within one business day.
                </p>
              </form>
            )}

            {/* Quick contact action buttons */}
            <div data-testid="quick-contact-actions" className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <QuickAction
                href={WHATSAPP_URL}
                target="_blank"
                testId="quick-whatsapp"
                icon={MessageCircle}
                label="WhatsApp Us"
                color="#25D366"
              />
              <QuickAction
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                testId="quick-call"
                icon={Phone}
                label="Call Now"
              />
              <QuickAction
                href={`mailto:${BRAND.email}`}
                testId="quick-email"
                icon={Mail}
                label="Send Email"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, type = "text", ...props }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] font-heading text-liftile-matte-black/65 mb-2 font-semibold">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        {...props}
        className="w-full bg-liftile-cream border border-black/10 rounded-lg px-4 py-3 text-liftile-matte-black focus:border-liftile-orange focus:ring-2 focus:ring-liftile-orange/15 focus:outline-none transition"
      />
    </div>
  );
}

function QuickAction({ href, target, testId, icon: Icon, label, color }) {
  return (
    <a
      href={href}
      target={target}
      rel={target ? "noopener noreferrer" : undefined}
      data-testid={testId}
      className="group inline-flex items-center justify-center gap-2 bg-liftile-matte-black text-liftile-cream font-heading font-semibold uppercase tracking-wider text-[11px] md:text-xs px-5 py-3.5 rounded-sm hover:bg-liftile-orange hover:-translate-y-0.5 transition-all duration-400"
    >
      <Icon className="w-4 h-4" style={color ? { color } : undefined} strokeWidth={2} />
      {label}
      <ArrowRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
    </a>
  );
}

/* ----------------- Factory visit + Google Map ----------------- */
function FactoryVisitSection() {
  return (
    <section data-testid="factory-visit" className="bg-liftile-black py-24 md:py-32 relative overflow-hidden">
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30" />
      <div
        aria-hidden
        className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none opacity-25"
        style={{
          background: "radial-gradient(circle, rgba(255,107,26,0.45) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(50px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl">
          <div className="heading-eyebrow mb-4">Visit Us</div>
          <h2
            data-testid="factory-visit-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          >
            Visit Our <span className="text-liftile-orange">Manufacturing Facility</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            Builders, contractors, developers, dealers and business partners are
            welcome to visit our manufacturing facility by scheduling an
            appointment with our team. Experience our manufacturing process,
            engineering standards and quality inspection process firsthand.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href={`mailto:${BRAND.email}?subject=${encodeURIComponent("Factory Visit Request")}&body=${encodeURIComponent("Hi LIFTILE,\\n\\nI'd like to schedule a visit to your manufacturing facility.\\n\\nName:\\nCompany:\\nPreferred date:\\nContact number:\\n\\nThank you.")}`}
              data-testid="factory-schedule-btn"
              className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-3.5 rounded-sm transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.85)]"
            >
              <Calendar className="w-4 h-4" />
              Schedule a Visit
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href={BRAND.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="factory-directions-btn"
              className="group inline-flex items-center justify-center gap-3 border border-white/40 bg-white/[0.04] backdrop-blur-sm text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-3.5 rounded-sm transition-all duration-400 hover:bg-white hover:text-liftile-matte-black hover:border-white hover:-translate-y-0.5"
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </a>
          </div>
        </motion.div>

        {/* Address card + Map */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          transition={{ delay: 0.1 }}
          className="mt-14 md:mt-16 bg-white/[0.04] border border-white/[0.07] rounded-2xl p-2 md:p-3 backdrop-blur-sm"
        >
          {/* Address row above map */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 px-4 md:px-5 pt-4 pb-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-liftile-orange/15 text-liftile-orange grid place-items-center shrink-0">
                <Building2 className="w-5 h-5" strokeWidth={1.7} />
              </div>
              <div>
                <div className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-orange/85 font-semibold mb-1">
                  Manufacturing Facility
                </div>
                <div className="font-body text-sm md:text-[15px] text-liftile-cream leading-snug whitespace-pre-line">
                  {BRAND.address}
                </div>
              </div>
            </div>
          </div>

          {/* Embedded Google Map */}
          <div className="relative aspect-[16/9] md:aspect-[16/8] w-full overflow-hidden rounded-xl">
            <iframe
              title="LIFTILE Manufacturing Facility — Google Maps"
              src={BRAND.mapsEmbedUrl}
              width="100%"
              height="100%"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full border-0 grayscale-[12%]"
              data-testid="factory-google-map"
            />
          </div>

          {/* Below map: two action buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 md:p-5">
            <a
              href={BRAND.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="factory-open-maps-btn"
              className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-6 py-3.5 rounded-sm transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_15px_35px_-10px_rgba(255,107,26,0.6)]"
            >
              <MapPin className="w-4 h-4" />
              Open in Google Maps
              <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href={BRAND.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="factory-navigate-btn"
              className="group inline-flex items-center justify-center gap-3 border border-white/30 bg-white/[0.04] text-liftile-cream font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-6 py-3.5 rounded-sm transition-all duration-400 hover:bg-white hover:text-liftile-matte-black hover:border-white hover:-translate-y-0.5"
            >
              <Navigation className="w-4 h-4" />
              Get Directions
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------- Download Company Profile ----------------- */
function DownloadProfileSection() {
  return (
    <section data-testid="download-profile" className="bg-liftile-cream py-20 md:py-28 relative overflow-hidden">
      <div className="container-x relative">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8 bg-white rounded-2xl p-8 md:p-10 lg:p-12 border border-black/[0.04] shadow-[0_10px_40px_-18px_rgba(0,0,0,0.15)]">
          <div className="flex-1">
            <div className="heading-eyebrow mb-3 text-liftile-orange">Brochure</div>
            <h2
              data-testid="download-profile-heading"
              className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-2xl sm:text-3xl md:text-4xl"
            >
              Learn More About <span className="text-liftile-orange">LIFTILE</span>
            </h2>
            <p className="font-body text-liftile-matte-black/70 mt-4 text-base leading-relaxed max-w-xl">
              Download our latest company profile to explore our products,
              manufacturing capabilities and engineering solutions.
            </p>
          </div>
          <a
            href="/liftile-brochure.pdf"
            download="LIFTILE-Product-Catalogue.pdf"
            data-testid="download-profile-btn"
            className="group inline-flex items-center justify-center gap-3 bg-liftile-orange text-white font-heading font-semibold uppercase tracking-wider text-xs md:text-sm px-7 py-4 rounded-sm transition-all duration-400 hover:-translate-y-0.5 hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.85)] shrink-0"
          >
            <Download className="w-4 h-4" />
            Download Company Profile
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ----------------- FAQ ----------------- */
const CONTACT_FAQS = [
  {
    q: "Do I need an appointment before visiting?",
    a: "Yes. We recommend scheduling an appointment so our engineering team can assist you efficiently.",
  },
  {
    q: "Can I visit the manufacturing facility?",
    a: "Yes. Customers, dealers and business partners are welcome to visit our manufacturing facility by prior appointment.",
  },
  {
    q: "Do you provide installation across India?",
    a: "Yes. We provide installation guidance and technical assistance for customers across India.",
  },
  {
    q: "How quickly will I receive a quotation?",
    a: "Most quotations are shared within one business day depending on project requirements.",
  },
  {
    q: "How can I become a LIFTILE dealer?",
    a: "Visit our Dealer page or contact our sales team to discuss dealership opportunities.",
  },
];

function ContactFAQSection() {
  return (
    <section data-testid="contact-faq" className="bg-liftile-black py-24 md:py-32 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full pointer-events-none opacity-[0.18]"
        style={{
          background: "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(40px)",
        }}
      />
      <div className="container-x relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl mx-auto text-center">
          <div className="heading-eyebrow mb-4">Common Questions</div>
          <h2
            data-testid="contact-faq-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          >
            Frequently Asked <span className="text-liftile-orange">Questions</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-5 text-base md:text-lg leading-relaxed mx-auto max-w-2xl">
            Find answers to common questions before contacting our team.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} transition={{ delay: 0.1 }} className="mt-12 md:mt-16 max-w-3xl mx-auto">
          <Accordion type="single" collapsible defaultValue="contact-faq-0" className="space-y-3">
            {CONTACT_FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`contact-faq-${i}`}
                data-testid={`contact-faq-item-${i}`}
                className="group/item border-0 bg-white/[0.04] backdrop-blur-sm rounded-xl border border-white/[0.06] hover:border-white/[0.12] transition-all duration-400 data-[state=open]:bg-white/[0.06] data-[state=open]:border-liftile-orange/55 data-[state=open]:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.3)]"
              >
                <AccordionTrigger
                  data-testid={`contact-faq-trigger-${i}`}
                  className="group/trigger w-full hover:no-underline [&>svg]:hidden px-6 md:px-7 py-5 md:py-6 text-left"
                >
                  <div className="flex items-start gap-5 w-full">
                    <span className="shrink-0 mt-1 font-heading text-xs uppercase tracking-[0.2em] text-liftile-orange font-semibold w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 font-heading text-base md:text-lg text-liftile-cream font-semibold leading-snug group-data-[state=open]/item:text-liftile-orange transition-colors duration-300">
                      {item.q}
                    </span>
                    <span
                      aria-hidden
                      className="shrink-0 grid place-items-center w-9 h-9 rounded-full border border-white/15 text-liftile-cream/70 group-data-[state=open]/item:bg-liftile-orange group-data-[state=open]/item:text-white group-data-[state=open]/item:border-liftile-orange group-data-[state=open]/item:rotate-45 transition-all duration-400 ease-out"
                    >
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 md:px-7 pb-6 md:pb-7 pt-0">
                  <div style={{ paddingLeft: "3.25rem" }}>
                    <div className="h-px bg-liftile-orange/30 mb-4" />
                    <p className="font-body text-sm md:text-base text-liftile-cream/75 leading-relaxed">
                      {item.a}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

/* ----------------- Why Contact LIFTILE ----------------- */
function WhyContactSection() {
  const cards = [
    { icon: Headphones, title: "Engineering Consultation", desc: "Receive expert guidance to select the right machine." },
    { icon: FileText,   title: "Fast Quotations",          desc: "Detailed quotations with technical specifications." },
    { icon: Building2,  title: "Nationwide Support",       desc: "Installation guidance and after-sales assistance." },
    { icon: Wrench,     title: "Trusted Manufacturing",    desc: "Premium branded components. Owner inspected before dispatch." },
  ];
  return (
    <section data-testid="why-contact" className="bg-liftile-cream py-24 md:py-32 relative overflow-hidden">
      <div className="container-x relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="max-w-3xl">
          <div className="heading-eyebrow mb-4 text-liftile-orange">Why LIFTILE</div>
          <h2
            data-testid="why-contact-heading"
            className="font-display uppercase text-liftile-matte-black leading-[0.95] tracking-tight text-3xl sm:text-4xl md:text-5xl"
          >
            Why <span className="text-liftile-orange">Choose LIFTILE?</span>
          </h2>
          <p className="font-body text-liftile-matte-black/70 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            From your first enquiry to after-sales support, we are committed to
            delivering dependable engineering solutions and outstanding customer
            service.
          </p>
        </motion.div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.55, ease: "easeOut" }}
                data-testid={`why-contact-card-${i}`}
                className="group relative bg-white rounded-2xl p-7 md:p-8 border border-black/[0.04] shadow-[0_4px_18px_-10px_rgba(0,0,0,0.10)] hover:shadow-[0_22px_50px_-15px_rgba(255,107,26,0.35)] hover:-translate-y-1 transition-all duration-400"
              >
                <div className="w-12 h-12 rounded-xl grid place-items-center border border-liftile-orange/35 text-liftile-orange mb-5 group-hover:bg-liftile-orange group-hover:text-white transition-all duration-400">
                  <Icon className="w-5 h-5" strokeWidth={1.8} />
                </div>
                <h3 className="font-heading uppercase text-base md:text-lg text-liftile-matte-black tracking-tight leading-snug">
                  {card.title}
                </h3>
                <div className="mt-3 w-10 h-0.5 bg-liftile-orange/70 transition-all duration-400 group-hover:w-16" />
                <p className="font-body text-[14px] text-liftile-matte-black/70 mt-3 leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ----------------- Response commitment strip ----------------- */
function ResponseCommitmentStrip() {
  return (
    <section
      data-testid="response-commitment"
      className="relative bg-liftile-matte-black py-14 md:py-16 border-y border-liftile-orange/15"
    >
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full pointer-events-none opacity-[0.18]"
        style={{
          background: "radial-gradient(ellipse, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(50px)",
        }}
      />
      <div className="container-x relative">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp} className="max-w-3xl mx-auto text-center">
          <div className="heading-eyebrow mb-3">Our Commitment</div>
          <p className="font-display uppercase text-liftile-cream text-2xl sm:text-3xl md:text-[2rem] leading-snug tracking-tight">
            We value your time.{" "}
            <span className="text-liftile-orange">
              Our team aims to respond to all genuine enquiries within one business day.
            </span>
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================== PAGE ============================== */
export default function ContactPage() {
  const seo = PAGE_SEO.contact;
  return (
    <>
      <Seo
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path={seo.path}
        image={seo.image}
        jsonLd={[
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
          buildFaqSchema(CONTACT_FAQS),
        ]}
        jsonLdId="contact"
      />
      <Navbar />
      <main>
        <ContactHero />
        <ContactInfoCards />
        <ContactFormBlock />
        <FactoryVisitSection />
        <DownloadProfileSection />
        <ContactFAQSection />
        <WhyContactSection />
        <ResponseCommitmentStrip />
      </main>

      <CTABanner
        headline="Let's Build Something Great Together"
        description="Whether you're planning a residential, commercial, industrial or infrastructure project, our engineering team is ready to help you choose the right construction machinery."
        secondaryLabel="Call Our Team"
        secondaryHref={`tel:${BRAND.phone.replace(/\s/g, "")}`}
        bgImage="/gallery-workshop-interior.png"
      />

      <Footer />
    </>
  );
}
